import {
  ChatAvailabilityResult,
  ChatContentResult,
  ChatContentResultTypenames,
  ChatEventType,
  ChatSender,
} from '@varo/graphql-schema-types';
import BotContent from 'features/chat/BotContent/BotContent';
import ChatBubble from 'features/chat/ChatBubble/ChatBubble';
import { formatChatHTMLMessage } from 'features/chat/helpers/Chat.helpers';
import HistoryBanner from 'features/chat/HistoryBanner/HistoryBanner';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import React, { useContext } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import { HTMLRenderer } from 'shared/components/HTMLRenderer/HTMLRenderer';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import theme from 'shared/styles/theme';
import { formatDate } from 'shared/utils/string/date';
import { Maybe } from 'shared/utils/types';

export interface CustomerChatMessageProps {
  sender: ChatSender;
  content?: Maybe<ChatContentResult>;
  eventType: ChatEventType;
  createdAt: string;
  chatAvailability?: ChatAvailabilityResult;
  id: string;
  isLatestBanner?: boolean;
  isDisabled?: boolean;
  index?: number;
}

const MessageItem = ({
  sender,
  content,
  eventType,
  createdAt,
  chatAvailability,
  isLatestBanner,
  isDisabled,
  index,
}: CustomerChatMessageProps) => {
  const { chatOutageMessage } = useContext(ChatContext);

  const { width } = useWindowDimensions();
  const position = sender === ChatSender.User ? 'right' : 'left';
  const isTyping = eventType === ChatEventType.AgentStartedTyping;

  const isBotSender = [ChatSender.LexBot, ChatSender.DecagonBot].includes(
    sender,
  );

  if (isBotSender) {
    return (
      <BotContent
        content={content}
        isTyping={isTyping}
        position={position}
        createdAt={createdAt}
        isDisabled={isDisabled}
        index={index}
      />
    );
  }

  switch (sender) {
    case ChatSender.FaqBot:
      return (
        <View>
          {content?.__typename ===
          ChatContentResultTypenames.ChatContentMessage ? (
            <ChatBubble
              isTyping={isTyping}
              position={position}
              createdAt={createdAt}
              isDisabled={isDisabled}
            >
              {content.message}
            </ChatBubble>
          ) : null}
          {content?.__typename ===
          ChatContentResultTypenames.ChatContentHtml ? (
            <ChatBubble
              isTyping={isTyping}
              position={position}
              createdAt={createdAt}
              isDisabled={isDisabled}
            >
              <HTMLRenderer
                htmlAsString={formatChatHTMLMessage(content.html)}
                contentWidth={width}
              />
            </ChatBubble>
          ) : null}
        </View>
      );
    case ChatSender.Agent:
      return (
        <>
          {content?.__typename ===
          ChatContentResultTypenames.ChatContentMessage ? (
            <ChatBubble
              isTyping={isTyping}
              position={position}
              createdAt={createdAt}
              isDisabled={isDisabled}
            >
              {content.message}
            </ChatBubble>
          ) : null}
          {content?.__typename ===
          ChatContentResultTypenames.ChatContentHtml ? (
            <BotContent
              content={content}
              isTyping={isTyping}
              position={position}
              createdAt={createdAt}
              isDisabled={isDisabled}
            />
          ) : null}
        </>
      );
    case ChatSender.User:
      return (
        <ChatBubble
          isTyping={isTyping}
          position={position}
          createdAt={createdAt}
          isDisabled={isDisabled}
        >
          {content?.__typename === ChatContentResultTypenames.ChatContentMessage
            ? content?.message
            : null}
        </ChatBubble>
      );
    case ChatSender.System:
      if (isTyping) {
        return (
          <ChatBubble
            isTyping={isTyping}
            position="left"
            createdAt={createdAt}
            hideTimeStamp
          >
            {content?.__typename ===
            ChatContentResultTypenames.ChatContentMessage
              ? content.message
              : null}
          </ChatBubble>
        );
      }
      return (
        <BodyXSText
          style={styles.date}
          data-testId="chatSystemMsg"
          numberOfLines={0}
        >
          {content?.__typename === ChatContentResultTypenames.ChatContentMessage
            ? content.message
            : null}
          {content?.__typename ===
          ChatContentResultTypenames.ChatContentHtml ? (
            <HTMLRenderer
              htmlAsString={formatChatHTMLMessage(content.html)}
              contentWidth={100}
            />
          ) : null}
        </BodyXSText>
      );

    case ChatSender.UndefinedChatSender:
      return (
        <View>
          <HistoryBanner
            chatAvailability={chatAvailability}
            unableToConnect={false}
            chatOutageMessage={chatOutageMessage}
            isLatestBanner={isLatestBanner}
          />
          <BodyXSText style={styles.date} numberOfLines={0}>
            {formatDate(createdAt, 'MMMM do, yyyy hh:mm aaa')}
          </BodyXSText>
        </View>
      );
    default:
      return null;
  }
};

const styles = StyleSheet.create({
  date: {
    paddingVertical: theme.spacing.m,
    textAlign: 'center',
  },
});

export default MessageItem;
